import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { useRecoilValue } from 'recoil';
import { layoutTypeState, logoTypeState } from '@atom/setting/homepageSet';
import { useDocument } from '@nandorojo/swr-firestore';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';
import { LOGO_TYPE, LOGO_TYPE_VALUE } from '@typings/setting/logoType';
import { LAYOUT_TYPE, LAYOUT_TYPE_VALUE } from '@typings/setting/layoutType';
import { RootPath } from '@routes/routePath';
import scrollTop from '@hooks/resetScrollTop';

const ImglogoSecMap = {
  [LAYOUT_TYPE.TOP]: css`
    height: 60px;
    margin-right: 12px;
    max-width: 220px;

    img {
      max-height: 80px;
    }
  `,
  [LAYOUT_TYPE.LEFT]: css`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
  `,
  [LAYOUT_TYPE.THREEDAN]: css`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
  `,
  [LAYOUT_TYPE.MINIHOME]: css`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    max-width: 220px;
  `,
};

const textlogoSecMap = {
  [LAYOUT_TYPE.TOP]: css`
    margin-right: 24px;
  `,
  [LAYOUT_TYPE.LEFT]: css`
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  [LAYOUT_TYPE.THREEDAN]: css`
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  [LAYOUT_TYPE.MINIHOME]: css`
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
};

const LogoSec = styled.figure<{ $layoutType?: LAYOUT_TYPE_VALUE }>`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 10;

  ${({ $layoutType }) => $layoutType && ImglogoSecMap[$layoutType]};

  img {
    max-width: 100%;
  }

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }

  @media (max-width: 768px) {
    height: 100%;
    padding: 0;
    margin-right: 0;
  }
`;

const TextLogoSec = styled.section<{ $layoutType?: LAYOUT_TYPE_VALUE }>`
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  text-align: center;
  word-break: keep-all;
  word-wrap: break;
  z-index: 1;
  color: var(--menuTextColor);
  h6 {
    font-size: 16px;
  }
  ${({ $layoutType }) => $layoutType && textlogoSecMap[$layoutType]}

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }
`;

interface MainLogoProps {
  style?: React.CSSProperties;
  customLogoType?: LOGO_TYPE_VALUE;
  imgStyle?: React.CSSProperties;
  onLogoClick?: () => void;
  [propName: string]: any;
}

function MainLogo({
  style,
  customLogoType,
  collectionPath,
  imgStyle,
  onLogoClick,
}: MainLogoProps): JSX.Element | null {
  const history = useNavigate();
  const logoType = customLogoType || useRecoilValue(logoTypeState);
  const layoutType = useRecoilValue(layoutTypeState);
  const { data: homeSet } = useDocument<IhomepageSetting>(
    `${RootPath.Setting}/homepageSetting`,
    { listen: true },
  );

  function handleLogoClick() {
    if (onLogoClick) {
      onLogoClick();
    } else {
      history(collectionPath ? `/${collectionPath}` : `/`);
    }
    scrollTop();
  }

  if (!logoType) return null;

  switch (logoType) {
    case LOGO_TYPE.TEXT:
      return (
        <TextLogoSec
          $layoutType={layoutType}
          onClick={handleLogoClick}
          style={style}
        >
          <h6>{homeSet?.homepageSet?.homepageTitle || ''}</h6>
        </TextLogoSec>
      );
    case LOGO_TYPE.IMAGE:
      return (
        <LogoSec $layoutType={layoutType} onClick={handleLogoClick} style={style}>
          <img
            src={homeSet?.homepageSet?.logoImgURL}
            alt="logo"
            loading="lazy"
            style={imgStyle}
          />
        </LogoSec>
      );
    case LOGO_TYPE.NONE:
    default:
      return null;
  }
}

export default MainLogo;
